import React from 'react';
import { DollarSign, X, Upload, Trash2, CheckCircle } from 'lucide-react';

const NewPaymentModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  versementData, 
  onInputChange, 
  modesPaiement, 
  documents, 
  onDocumentUpload, 
  onDocumentRemove, 
  versementLoading,
  studentName,
  paymentOptions,
  inscription
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        <div className="bg-[#202654] text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Nouveau versement</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          <div className="space-y-5">
            <div>
              <label htmlFor="payment_type" className="block text-sm font-medium text-gray-700 mb-1">
                Type de paiement <span className="text-red-500">*</span>
              </label>
              <select
                id="payment_type"
                name="payment_type"
                value={versementData.payment_type}
                onChange={onInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                required
              >
                <option value="">Sélectionner un type de paiement</option>
                <option value="SCOLARITE">Frais de scolarité</option>
                {!inscription?.frais_inscription_paye && 
                  <option value="INSCRIPTION">Frais d'inscription</option>
                }
                {paymentOptions?.map((option) => (
                  <option key={`option-${option.id}`} value={`OPTION-${option.id}`}>
                    {option.option_libelle} ({option.option_prix} FCFA)
                  </option>
                ))}
              </select>
            </div>

            {versementData.payment_type && versementData.payment_type.startsWith('OPTION') && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                <p className="text-sm text-blue-800">
                  Vous effectuez un paiement pour une option complémentaire.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-1">
                Montant (FCFA) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="montant"
                  name="montant"
                  value={versementData.montant}
                  onChange={onInputChange}
                  className="block w-full px-4 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                  required
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="mode_paiement" className="block text-sm font-medium text-gray-700 mb-1">
                Mode de paiement <span className="text-red-500">*</span>
              </label>
              <select
                id="mode_paiement"
                name="mode_paiement"
                value={versementData.mode_paiement}
                onChange={onInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                required
              >
                <option value="">Sélectionner un mode de paiement</option>
                {modesPaiement.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.libelle}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="commentaire" className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire
              </label>
              <textarea
                id="commentaire"
                name="commentaire"
                value={versementData.commentaire}
                onChange={onInputChange}
                rows="2"
                placeholder="Ajoutez des détails sur ce versement..."
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documents justificatifs (optionnel)
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['fichier1', 'fichier2'].map((field, index) => (
                  <div key={field} className="relative">
                    {documents[field] ? (
                      <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
                        <div className="truncate text-xs flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                          {documents[field].name}
                        </div>
                        <button
                          type="button"
                          onClick={() => onDocumentRemove(field)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-16 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col items-center justify-center">
                          <Upload className="w-5 h-5 mb-1 text-gray-500" />
                          <p className="text-xs text-gray-500 text-center">
                            <span className="font-semibold">Document {index + 1}</span>
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => onDocumentUpload(e, field)}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Formats acceptés: PDF, JPG, JPEG, PNG</p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r my-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    Ce versement sera enregistré pour l'étudiant {studentName}.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={versementLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50"
            >
              {versementLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2 inline-block" />
                  Effectuer le versement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPaymentModal;
